import { CircularProgress, Grid } from "@material-ui/core";
import axios from "axios";
import {
	CategoryScale,
	type ChartData,
	Chart as ChartJS,
	Legend,
	LineElement,
	LinearScale,
	PointElement,
	TimeScale,
	Title,
	Tooltip,
} from "chart.js";
import "chartjs-adapter-moment/dist/chartjs-adapter-moment.esm.js";
import zoomPlugin from "chartjs-plugin-zoom";
import React, { useEffect, useRef, useState } from "react";
import TableComponent from "./TableComponent";
import { useTrendingContext } from "./context/TrendingContext";
import { CursorData, ViewTag, ViewTagDataSet } from "./models/TrendingModels";

import { isNumber } from "lodash-es";
import { ErrorModal } from "../../../../components/modals/error-modal";
import { useChartOptions } from "./sections/chart/hooks/useChartOptions";
import { Header } from "./sections/header";
import { TrendingChart } from "./sections/trending-chart";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	TimeScale,
	zoomPlugin,
);

interface TrendingProps {
	title: string;
	getTagsFromAPI: Function;
}

const Trending: React.FC<TrendingProps> = ({ title, getTagsFromAPI }) => {
	const {
		state,
		state: { viewTags, shouldRefetchSeries },
	} = useTrendingContext();

	const [error, setError] = useState("");
	const [loading, setLoading] = useState(true);

	const [series, setSeries] = useState<ViewTagDataSet[][]>([]);
	const [selectedRowTagId, setSelectedRowTagId] = useState<number | null>(null);
	const [viewTagForDelete, setViewTagForDelete] = useState<ViewTag | null>(
		null,
	);

	const [chartOptions, setChartOptions] = useChartOptions();
	const [chartData, setChartData] = useState<ChartData<"line">>({
		labels: [],
		datasets: [],
	});
	const [cursorData, setCursorData] = useState<CursorData | null>(null);

	const debounceForFetchingData = useRef<NodeJS.Timeout | null>(null);

	const [contextMenuPosition, setContextMenuPosition] = useState({
		x: 0,
		y: 0,
	});
	const [openContextMenu, setOpenContextMenu] = useState(false);

	const handleRightClick = (event: any, tagId: number | null) => {
		event.preventDefault();
		event.stopPropagation(); // Prevent the click event from propagating to the document

		if (isNumber(tagId)) {
			setViewTagForDelete(viewTags[tagId].viewTag);
		} else {
			setViewTagForDelete(null);
		}
		setSelectedRowTagId(tagId);
		setOpenContextMenu(true);
		setContextMenuPosition({ x: event.clientX, y: event.clientY });
	};

	useEffect(() => {
		if (debounceForFetchingData.current) {
			clearTimeout(debounceForFetchingData.current);
		}
		debounceForFetchingData.current = setTimeout(() => {
			const fetchData = async () => {
				try {
					setLoading(true);
					const viewTagsArr = Object.values(viewTags).sort(
						(a, b) => b.order - a.order,
					);
					const tagIds: number[] = viewTagsArr.map((tag) => tag.viewTag.TagId);
					if (tagIds.length) {
						const response = await axios.post("/series?nodeName=null", {
							start: state.timeScopeStart.getTime(),
							end: state.timeScopeEnd.getTime(),
							tagIds: tagIds,
							sampleCount: 1000,
						});
						setSeries(response.data || []);
					}
					setLoading(false);
				} catch (error: any) {
					setError(error.message);
					setLoading(false);
				}
			};

			fetchData();
		}, 800);
		return () => {
			if (debounceForFetchingData.current) {
				clearTimeout(debounceForFetchingData.current);
			}
		};
	}, [shouldRefetchSeries]);

	return (
		<>
			<Grid
				container
				style={{ width: "95svw", height: "88svh", margin: 0 }}
				onClick={() => setOpenContextMenu(false)}
			>
				<Header setChartOptions={setChartOptions} />
				<Grid item xs={12} style={{ height: "45%" }}>
					<TrendingChart
						series={series}
						chartData={chartData}
						setChartData={setChartData}
						chartOptions={chartOptions}
						setChartOptions={setChartOptions}
						setCursorData={setCursorData}
					/>
				</Grid>
				<Grid
					item
					container
					xs={12}
					onContextMenu={(e) => handleRightClick(e, null)}
					style={{
						height: "40%",
						minWidth: "100%",
						paddingTop: "3rem",
						border: "4px solid #ccc",
						borderRadius: 10,
						padding: 10,
						margin: "20px 0 10px",
						position: loading ? "relative" : "inherit",
					}}
				>
					{loading && (
						<div
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								height: "100%",
								background: "rgba(255, 255, 255, 0.7)", // fondo semitransparente
								display: "flex", // Centra el CircularProgress
								justifyContent: "center", // Centra horizontalmente
								alignItems: "center", // Centra verticalmente
								zIndex: 2,
							}}
						>
							<CircularProgress size="2rem" />
						</div>
					)}
					<TableComponent
						chartData={chartData}
						cursorData={cursorData as any}
						getTagsFromAPI={getTagsFromAPI}
						openContextMenu={openContextMenu}
						setOpenContextMenu={setOpenContextMenu}
						viewTagForDelete={viewTagForDelete}
						setViewTagForDelete={setViewTagForDelete}
						selectedRowTagId={selectedRowTagId}
						setSelectedRowTagId={setSelectedRowTagId}
						handleRightClick={handleRightClick}
						contextMenuPosition={contextMenuPosition}
						setContextMenuPosition={setContextMenuPosition}
					/>
				</Grid>
			</Grid>
			<ErrorModal error={error} onHide={() => setError("")} title="ERROR" />
		</>
	);
};

export default Trending;
